/** Demo Mode only - shared scope + filter state for the Finance area, backed by URL search
 * params so the scope (Company/Client/Contract/Site) and filter bar selections persist as
 * the user moves between the Overview/Forecast/Profit & Loss/Contracts/EBIT Analysis tabs,
 * and are shareable/bookmarkable via the URL. */

import { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  CommercialModel, ContractSector, FINANCE_CONTRACTS, FinanceContract,
} from '../../mocks/data/financeData';
import { ComparisonBasis, FinanceFilterState, FinanceView, ScopeLevel } from './types';

const DEFAULT_FILTERS: FinanceFilterState = {
  scopeLevel: 'company',
  scopeId: 'all',
  view: 'ytd',
  comparison: 'budget',
  sector: '',
  region: '',
  commercialModel: '',
};

export interface FinanceFiltersContextValue extends FinanceFilterState {
  setScopeLevel: (level: ScopeLevel) => void;
  setScopeId: (id: string) => void;
  setView: (view: FinanceView) => void;
  setComparison: (comparison: ComparisonBasis) => void;
  setSector: (sector: ContractSector | '') => void;
  setRegion: (region: string) => void;
  setCommercialModel: (model: CommercialModel | '') => void;
  clearFilters: () => void;
  /** Contracts matching the current scope + sector/region/commercial-model filters. */
  scopedContracts: FinanceContract[];
  /** The single contract selected when scopeLevel is 'contract' or 'site' - undefined otherwise. */
  selectedContract?: FinanceContract;
  scopeLabel: string;
}

const FinanceFiltersContext = createContext<FinanceFiltersContextValue | undefined>(undefined);

const PARAM_KEYS = {
  scopeLevel: 'financeScopeLevel',
  scopeId: 'financeScopeId',
  view: 'financeView',
  comparison: 'financeComparison',
  sector: 'financeSector',
  region: 'financeRegion',
  commercialModel: 'financeModel',
} as const;

export const FinanceFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: FinanceFilterState = {
    scopeLevel: (searchParams.get(PARAM_KEYS.scopeLevel) as ScopeLevel) || DEFAULT_FILTERS.scopeLevel,
    scopeId: searchParams.get(PARAM_KEYS.scopeId) || DEFAULT_FILTERS.scopeId,
    view: (searchParams.get(PARAM_KEYS.view) as FinanceView) || DEFAULT_FILTERS.view,
    comparison: (searchParams.get(PARAM_KEYS.comparison) as ComparisonBasis) || DEFAULT_FILTERS.comparison,
    sector: (searchParams.get(PARAM_KEYS.sector) as ContractSector) || '',
    region: searchParams.get(PARAM_KEYS.region) || '',
    commercialModel: (searchParams.get(PARAM_KEYS.commercialModel) as CommercialModel) || '',
  };

  const setParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      return next;
    }, { replace: true });
  };

  const scopedContracts = useMemo(() => FINANCE_CONTRACTS.filter((contract) => {
    if (filters.sector && contract.sector !== filters.sector) return false;
    if (filters.region && contract.region !== filters.region) return false;
    if (filters.commercialModel && contract.commercialModel !== filters.commercialModel) return false;
    if (filters.scopeLevel === 'client') return contract.client === filters.scopeId;
    if (filters.scopeLevel === 'contract' || filters.scopeLevel === 'site') {
      return contract.id === filters.scopeId || contract.site === filters.scopeId;
    }
    return true;
  }), [filters.sector, filters.region, filters.commercialModel, filters.scopeLevel, filters.scopeId]);

  const selectedContract = (filters.scopeLevel === 'contract' || filters.scopeLevel === 'site')
    ? scopedContracts[0]
    : undefined;

  const scopeLabel = selectedContract
    ? `${selectedContract.client} - ${selectedContract.contractName}`
    : (filters.scopeLevel === 'client' ? filters.scopeId : t('financeScopeAllContracts', 'All contracts (company)'));

  const value: FinanceFiltersContextValue = {
    ...filters,
    setScopeLevel: (level) => setParam(PARAM_KEYS.scopeLevel, level === 'company' ? '' : level),
    setScopeId: (id) => setParam(PARAM_KEYS.scopeId, id === 'all' ? '' : id),
    setView: (view) => setParam(PARAM_KEYS.view, view === 'ytd' ? '' : view),
    setComparison: (comparison) => setParam(PARAM_KEYS.comparison, comparison === 'budget' ? '' : comparison),
    setSector: (sector) => setParam(PARAM_KEYS.sector, sector),
    setRegion: (region) => setParam(PARAM_KEYS.region, region),
    setCommercialModel: (model) => setParam(PARAM_KEYS.commercialModel, model),
    clearFilters: () => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.values(PARAM_KEYS).forEach((key) => next.delete(key));
        return next;
      }, { replace: true });
    },
    scopedContracts,
    selectedContract,
    scopeLabel,
  };

  return <FinanceFiltersContext.Provider value={value}>{children}</FinanceFiltersContext.Provider>;
};

export const useFinanceFilters = (): FinanceFiltersContextValue => {
  const ctx = useContext(FinanceFiltersContext);
  if (!ctx) {
    throw new Error('useFinanceFilters must be used within a FinanceFiltersProvider');
  }
  return ctx;
};
